import { getAssetPreviewKind } from '@setvi/shared/utils';
import { PdfPreviewContent } from 'componentsV2/pdf-preview-dialog';

import {
  AudioContent,
  ImageContent,
  OfficeContent,
  UnavailableContent,
  VideoContent
} from './components';
import type { PreviewAsset } from './types';
import { openProductAssetFile } from './utils/open-product-asset';

interface AssetPreviewProps {
  asset: PreviewAsset;
}

/**
 * Inline preview of a product asset by its kind: the print-order PDF canvas
 * (zoom, pager) for PDFs, a zoomable image, the browser's video or audio
 * player, the Office Online viewer for Word / Excel / PowerPoint. Anything
 * else (fonts, TIFF, …) shows a "not available" state with an explicit
 * "Open file" action — the click that got here never downloads by itself.
 */
export const AssetPreview = ({ asset }: AssetPreviewProps) => {
  const { url, name } = asset;

  if (!url) return <UnavailableContent />;

  const openFile = () => openProductAssetFile(url);

  switch (getAssetPreviewKind(asset)) {
    case 'pdf':
      return <PdfPreviewContent pages={[{ label: name, pdfUrl: url }]} />;
    case 'image':
      return <ImageContent url={url} name={name} onOpenFile={openFile} />;
    case 'video':
      return <VideoContent url={url} onOpenFile={openFile} />;
    case 'audio':
      return <AudioContent url={url} />;
    case 'office':
      return <OfficeContent url={url} name={name} />;
    default:
      return <UnavailableContent onOpenFile={openFile} />;
  }
};
